import React from "react";
import FeatherIcon from "feather-icons-react";
import { useTranslation } from "react-i18next";
import {
  ApplicationFeature,
  CompanyFeatureFunctionalityDto,
  UserRole,
} from "@green0meter/api-client";

import SidebarSectionItem from "./components/SidebarSection/components/SidebarSectionItem";

import { useUserStore } from "@store/user";
import {
  useCreateFormConfig,
  useGetEmissionFactors,
  useGetProcurementSupplierHubs,
  useGetQuestionsAssignmentFormsBasics,
  useUpdateCompany,
} from "@api";
import { hasFeature } from "@helpers";

export type SidebarSections = {
  top: SidebarSectionItem[];
  middle: SidebarSectionItem[];
  bottom: SidebarSectionItem[];
};

export const getSidebarSections = (
  features?: CompanyFeatureFunctionalityDto[],
): SidebarSections => {
  const { t } = useTranslation();
  const { user } = useUserStore();
  const { updateCompanyEnabled } = useUpdateCompany(user);
  const { createFormConfigEnabled } = useCreateFormConfig(user);
  const { questionsAssignmentFormsBasicsEnabled } =
    useGetQuestionsAssignmentFormsBasics(user);
  const { emissionFactorsEnabled } = useGetEmissionFactors(user);
  const { hubs } = useGetProcurementSupplierHubs();

  const isSupplier = hasFeature(features, ApplicationFeature.PROCUREMENT_SUPPLIER);

  const isBuyer = hasFeature(features, ApplicationFeature.PROCUREMENT_BUYER);

  const isBuyerAndSupplier = isSupplier && isBuyer;

  const isRespondent = user?.role === UserRole.Respondent;
  const isViewer = user?.role === UserRole.Viewer;
  const isAuditor = user?.role === UserRole.Auditor;

  const top: SidebarSectionItem[] = [
    {
      title: t("menu.home"),
      path: "/",
      isVisible: isBuyerAndSupplier || !isSupplier,
      icon: <FeatherIcon icon="home" size={20} />,
    },
    {
      title: "GM Admin",
      path: "/admin/entities",
      isVisible: user?.role === UserRole.Admin,
      children: [
        {
          title: t("admin.entitiesManagement.title"),
          path: "/admin/entities",
          isVisible: true,
        },
        {
          title: t("admin.emissionFactorsManagement.title"),
          path: "/admin/emission-factors",
          isVisible: true,
        },
      ],
      icon: <FeatherIcon icon="alert-circle" size={20} />,
    },
    {
      title: t("menu.servicesMarketplace", "Services Marketplace"),
      path: "/services-marketplace",
      isVisible:
        user?.role !== UserRole.Respondent &&
        user?.role !== UserRole.Viewer &&
        user?.role !== UserRole.Auditor,
      icon: <FeatherIcon icon="grid" size={20} />,
    },
    {
      title: t("menu.co2hub"),
      path: "/co2",
      isVisible: hasFeature(features, ApplicationFeature.CO2),
      children: [
        {
          isVisible: !isRespondent,
          title: t("common.dashboard"),
          path: "/co2/dashboard",
        },
      ],
      icon: <FeatherIcon icon="cloud" size={20} />,
    },
    {
      title: t("menu.csrdHub"),
      path: "/csrd",
      isVisible: hasFeature(features, ApplicationFeature.CSRD),
      children: [
        {
          title: t("menu.doubleMateriality"),
          path: "/csrd/double-materiality",
          isVisible: !isViewer,
        },
        // {
        //   title: t("menu.euTaxonomy"),
        //   path: "/csrd/eu-taxonomy",
        // },
        {
          title: t("common.dashboard"),
          path: "/csrd/dashboard",
          isVisible: !isRespondent,
        },
      ],
      icon: <FeatherIcon icon="pie-chart" size={20} />,
    },
    {
      title: t("menu.brsrHub"),
      path: "/brsr",
      isVisible: hasFeature(features, ApplicationFeature.BRSR),
      children: [
        {
          title: t("common.dashboard"),
          path: "/brsr/dashboard",
          isVisible: !isRespondent,
        },
      ],
      icon: <FeatherIcon icon="bar-chart-2" size={20} />,
    },
    {
      title: t("menu.csobHub"),
      path: "/csob",
      isVisible: hasFeature(features, ApplicationFeature.CSOB),
      children: [],
      icon: <FeatherIcon icon="divide" size={20} />,
    },
    {
      title: t("menu.csobSkHub"),
      path: "/csob-sk",
      isVisible: hasFeature(features, ApplicationFeature.CSOB_SK),
      children: [],
      icon: <FeatherIcon icon="credit-card" size={20} />,
    },
    {
      title: t("menu.suppliersHub"),
      path: "/buyers-hub",
      isVisible:
        hasFeature(features, ApplicationFeature.PROCUREMENT_BUYER) &&
        !isRespondent,
      children: [],
      icon: <FeatherIcon icon="shopping-bag" size={20} />,
    },
    {
      title: t("menu.vsmeHub"),
      path: "/vsme",
      isVisible: hasFeature(features, ApplicationFeature.VSME),
      children: [
        {
          title: t("common.dashboard"),
          path: "/vsme/dashboard",
          isVisible: !isRespondent,
        },
      ],
      icon: <FeatherIcon icon="users" size={20} />,
    },
    {
      title: t("menu.paygap"),
      path: "/paygap",
      isVisible: hasFeature(features, ApplicationFeature.PAYGAP),
      children: [],
      icon: <FeatherIcon icon="users" size={20} />,
    },
  ];

  const middle: SidebarSectionItem[] = (hubs ?? []).map((hub) => ({
    title: `${hub?.supplierHubName ?? ""} ${t("menu.supplierHub")}`.trim(),
    path: `/suppliers-hub/${hub.procurementId}`,
    isVisible: hasFeature(features, ApplicationFeature.PROCUREMENT_SUPPLIER),
    icon: <FeatherIcon icon="dollar-sign" size={20} />,
  }));

  const bottom: SidebarSectionItem[] = [
    {
      title: t("menu.documentManagementHub"),
      path: "/document-management-hub",
      isVisible: (isBuyerAndSupplier || !isSupplier) && !isViewer,
      hasChip: true,
      icon: <FeatherIcon icon="file-text" size={20} />,
    },
    {
      title: t("menu.orgnanization"),
      path: "/organization/users",
      isVisible: updateCompanyEnabled && !isRespondent && !isViewer && !isAuditor,
      icon: <FeatherIcon icon="briefcase" size={20} />,
    },
    {
      title: t("menu.formBuilder"),
      path: "/form-builder",
      isVisible:
        createFormConfigEnabled &&
        (isBuyerAndSupplier || !isSupplier) &&
        (user?.role === UserRole.Admin || user?.role === UserRole.Owner),
      icon: <FeatherIcon icon="layers" size={20} />,
    },
    {
      title: t("menu.questionAssignment"),
      path: "/question-assignment",
      isVisible:
        questionsAssignmentFormsBasicsEnabled &&
        (isBuyerAndSupplier || !isSupplier) &&
        (user?.role === UserRole.Admin || user?.role === UserRole.Owner),
      icon: <FeatherIcon icon="check-square" size={20} />,
    },
    {
      title: t("menu.emissionFactors"),
      path: "/emission-factors",
      isVisible:
        hasFeature(features, ApplicationFeature.EF_MANAGEMENT) &&
        emissionFactorsEnabled &&
        !isRespondent &&
        !isViewer &&
        !isAuditor,
      icon: <FeatherIcon icon="cloud-rain" size={20} />,
    },
  ];

  return { top, middle, bottom };
};
